/**
 * PATH: packages/ui/src/components/find/findHighlightRanges.ts
 * FILE_ID: UI-FIND-HIGHLIGHT-RANGES-001
 * PURPOSE: Paint in-page find hits with the CSS Custom Highlight API —
 *          Ranges registered on CSS.highlights, styled by ::highlight().
 *          Nothing inside the searched container is ever mutated.
 * WHY:     The <mark> highlighter splits the container's text nodes. When
 *          that container holds React-rendered content (the email thread
 *          re-renders on every keystroke as find expands quoted blocks),
 *          React's next commit calls removeChild() on a text node we
 *          already replaced and throws NotFoundError, unmounting the tree
 *          and blanking the screen. Ranges are inert observers of the DOM,
 *          so React keeps ownership of every node and the crash is gone.
 * FLOW:
 *   ┌───────────────┐   ┌──────────────┐   ┌───────────────────────┐
 *   │ container +   │ → │ scan text →  │ → │ CSS.highlights.set(   │
 *   │ query         │   │ Range[]      │   │  hit / active )       │
 *   └───────────────┘   └──────────────┘   └───────────────────────┘
 * DEPENDENCIES:
 *   - ./findMatchScan: shared needle/text-node/offset scanner.
 * RELATED:
 *   - findHighlightDom.ts: the <mark> fallback for browsers without the API.
 *   - findHighlightPainter.ts: picks between the two at runtime.
 */
import {
  collectMatchTextNodes,
  matchOffsets,
  normaliseNeedle,
} from './findMatchScan'

/** Registry keys. Must match the ::highlight() selectors injected below. */
export const FIND_HIT_HIGHLIGHT = 'qt-find-hit'
export const FIND_ACTIVE_HIGHLIGHT = 'qt-find-active'

const STYLE_ELEMENT_ID = 'qt-find-highlight-style'

// ::highlight() only honours colour-ish properties (no radius/shadow), so
// this is the closest match to the <mark> fallback's yellow/orange pair.
const HIGHLIGHT_CSS = `
::highlight(${FIND_HIT_HIGHLIGHT}) {
  background-color: rgba(253, 224, 71, 0.75);
  color: inherit;
}
::highlight(${FIND_ACTIVE_HIGHLIGHT}) {
  background-color: rgba(251, 146, 60, 0.95);
  color: #1f2937;
}
`

/** True when this browser can paint highlights without touching the DOM. */
export function isRangeHighlightSupported(): boolean {
  return (
    typeof CSS !== 'undefined' &&
    typeof CSS.highlights !== 'undefined' &&
    CSS.highlights !== null &&
    typeof Highlight === 'function'
  )
}

/**
 * Inject the ::highlight() rules once per document. Lives in <head>, well
 * outside any React root, so it can never disturb a reconciliation.
 */
export function ensureHighlightStyles(doc: Document = document): void {
  if (doc.getElementById(STYLE_ELEMENT_ID)) return
  const style = doc.createElement('style')
  style.id = STYLE_ELEMENT_ID
  style.textContent = HIGHLIGHT_CSS
  doc.head.appendChild(style)
}

/**
 * Every hit of `rawQuery` inside `container`, in document order, as
 * Ranges. Read-only: the container is scanned, never modified.
 */
export function collectMatchRanges(
  container: HTMLElement,
  rawQuery: string,
): Range[] {
  const needle = normaliseNeedle(rawQuery)
  if (!needle) return []
  const ranges: Range[] = []
  for (const textNode of collectMatchTextNodes(container, needle)) {
    const value = textNode.nodeValue ?? ''
    for (const offset of matchOffsets(value, needle)) {
      const range = document.createRange()
      range.setStart(textNode, offset)
      range.setEnd(textNode, offset + needle.length)
      ranges.push(range)
    }
  }
  return ranges
}

/**
 * Register `ranges` as the hit highlight and the one at `activeIndex` as
 * the active highlight. Registering the active set last gives it painting
 * priority over the hit set it overlaps.
 */
export function paintRangeHighlights(
  ranges: Range[],
  activeIndex: number,
): void {
  if (!isRangeHighlightSupported()) return
  ensureHighlightStyles()
  const registry = CSS.highlights
  if (ranges.length === 0) {
    registry.delete(FIND_HIT_HIGHLIGHT)
    registry.delete(FIND_ACTIVE_HIGHLIGHT)
    return
  }
  registry.set(FIND_HIT_HIGHLIGHT, new Highlight(...ranges))
  const active = ranges[activeIndex]
  if (active) {
    registry.set(FIND_ACTIVE_HIGHLIGHT, new Highlight(active))
  } else {
    registry.delete(FIND_ACTIVE_HIGHLIGHT)
  }
}

/** Drop both highlight registrations. Safe to call when none exist. */
export function clearRangeHighlights(): void {
  if (!isRangeHighlightSupported()) return
  CSS.highlights.delete(FIND_HIT_HIGHLIGHT)
  CSS.highlights.delete(FIND_ACTIVE_HIGHLIGHT)
}

/**
 * Bring a Range's hit on screen. A Range has no scrollIntoView, and
 * inserting a scroll anchor would reintroduce the DOM mutation this
 * module exists to avoid, so we scroll the element that contains it.
 */
export function scrollRangeIntoView(range: Range | undefined): void {
  if (!range) return
  const node = range.startContainer
  const element =
    node.nodeType === Node.ELEMENT_NODE
      ? (node as HTMLElement)
      : node.parentElement
  // Guarded: a hit can survive into a detached node between a React commit
  // and our re-scan, and scrolling is the least important thing find does —
  // it must never be the thing that throws.
  if (typeof element?.scrollIntoView !== 'function') return
  element.scrollIntoView({ block: 'center', behavior: 'smooth' })
}
